import {useEffect, useRef, useCallback, ReactNode} from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Skeleton,
} from "@mui/material";

interface InfiniteScrollListProps<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  renderItem: (item: T, index: number) => ReactNode;
  emptyMessage?: string;
  loadingMessage?: string;
  threshold?: number; // Distance from bottom to trigger load more (in pixels)
  skeletonCount?: number;
  renderSkeleton?: () => ReactNode;
}

/**
 * Reusable infinite scroll list component
 * Handles scroll detection, loading states, and error states
 */
function InfiniteScrollList<T>({
  data,
  loading,
  error,
  hasMore,
  onLoadMore,
  renderItem,
  emptyMessage = "No items found",
  loadingMessage = "Loading more items...",
  threshold = 200,
  skeletonCount = 3,
  renderSkeleton,
}: InfiniteScrollListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Handle intersection observer for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      {
        rootMargin: `${threshold}px`,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
    };
  }, [loading, hasMore, onLoadMore, threshold]);

  // Handle scroll to load more (backup method)
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || loading || !hasMore) return;

    const {scrollTop, scrollHeight, clientHeight} = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < threshold) {
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore, threshold]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Default skeleton renderer
  const defaultSkeleton = useCallback(
    () => (
      <Box sx={{p: 2}}>
        <Skeleton variant="text" height={40} width="60%" />
        <Skeleton variant="text" height={20} width="40%" />
        <Skeleton variant="rectangular" height={60} sx={{mt: 1}} />
      </Box>
    ),
    []
  );

  const skeletonRenderer = renderSkeleton || defaultSkeleton;

  // Show error state
  if (error && data.length === 0) {
    return (
      <Alert severity="error" sx={{m: 2}}>
        {error}
      </Alert>
    );
  }

  // Show empty state
  if (!loading && data.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 4,
          color: "text.secondary",
        }}
      >
        <Typography variant="body1">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        overflowY: "auto",
        position: "relative",
      }}
    >
      {/* Render items */}
      {data.map((item, index) => (
        <Box key={`item-${index}`} sx={{mb: 2}}>
          {renderItem(item, index)}
        </Box>
      ))}

      {/* Show skeleton loading for initial load */}
      {loading && data.length === 0 && (
        <Box>
          {Array.from({length: skeletonCount}, (_, i) => (
            <Box key={`skeleton-${i}`} sx={{mb: 2}}>
              {skeletonRenderer()}
            </Box>
          ))}
        </Box>
      )}

      {/* Loading more indicator */}
      {loading && data.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 2,
            gap: 1,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">
            {loadingMessage}
          </Typography>
        </Box>
      )}

      {/* Error during load more */}
      {error && data.length > 0 && (
        <Alert severity="error" sx={{m: 2}}>
          {error}
        </Alert>
      )}

      {/* No more items indicator */}
      {!loading && !hasMore && data.length > 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 2,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">No more items to load</Typography>
        </Box>
      )}

      {/* Intersection observer sentinel */}
      <div
        ref={sentinelRef}
        style={{
          height: "1px",
          visibility: "hidden",
        }}
      />
    </Box>
  );
}

export default InfiniteScrollList;
